import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger
} from '@heroui/react';
import { NavArrowDown } from 'iconoir-react';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';

import useEnvironments from '~/common/hooks/useEnvironments';
import {
  ENV_INDICATOR_COLORS,
  type EnvironmentType
} from '~/components/EnvironmentIndicator';
import { StoreRootState } from '~/scenes/Governance/types/StoreRootState';

export { ENV_INDICATOR_COLORS };

type EnvironmentSelectProps = {
  type?: 'application' | 'apiKeys';
};

const indicatorClassName = (environment: string) =>
  (ENV_INDICATOR_COLORS as Record<string, string>)[environment] ??
  'bg-default-500';

export const EnvironmentSelect = ({
  type = 'application'
}: EnvironmentSelectProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { application, apiKeys } = useSelector<
    StoreRootState,
    Pick<StoreRootState, 'application' | 'apiKeys'>
  >(state => state);
  const dispatch = useDispatch();
  const [filteredEnvironments] = useEnvironments('APIKEY:READ');
  const valuesByType = useMemo(
    () => ({
      application: {
        environments: application.environments?.allNames?.asMutable({
          deep: true
        }),
        environment: application.environment
      },
      apiKeys: {
        environments: filteredEnvironments.map(
          (env: { name: string }) => env?.name
        ) as string[],
        environment: apiKeys.environment
      }
    }),
    [
      apiKeys.environment,
      application.environment,
      application.environments?.allNames,
      filteredEnvironments
    ]
  );

  const changeEnvironment = (value: string) => {
    if (type === 'apiKeys') {
      dispatch.apiKeys.setEnvironment(value);
      return;
    }

    const pathParts = location.pathname.split('/');

    const envIndex = pathParts.findIndex(part =>
      valuesByType[type].environments?.includes(part)
    );

    if (envIndex < 0) {
      return;
    }

    pathParts[envIndex] = value;
    const newPath = pathParts.join('/');
    const newUrl = `${newPath}${window.location.search || ''}`;
    navigate(newUrl, { replace: true });
  };

  const sortedEnvs = useMemo(() => {
    if (!valuesByType[type].environments.length) return [];

    const envNames = valuesByType[type].environments;

    const testIndex = envNames.indexOf('test');
    if (testIndex > -1) {
      envNames.splice(testIndex, 1);
      envNames.unshift('test');
    }
    const prodIndex = envNames.indexOf('prod');
    if (prodIndex > -1) {
      envNames.splice(prodIndex, 1);
      envNames.splice(1, 0, 'prod');
    }
    return envNames as EnvironmentType[];
  }, [type, valuesByType]);

  const currentEnv = valuesByType[type].environment as EnvironmentType;
  const selectedEnv = (
    sortedEnvs.includes(currentEnv) ? currentEnv : sortedEnvs[0]
  ) as EnvironmentType;

  return (
    <Dropdown placement='bottom-start'>
      <DropdownTrigger>
        <button
          type='button'
          data-testid='operation-header-enviroment-list'
          className='flex h-8 items-center gap-2 rounded-md border border-outline-hover bg-surface px-2 py-1 outline-none'
        >
          <span className='flex items-center gap-1.5'>
            <span
              className={`size-1.5 rounded-full ${indicatorClassName(selectedEnv)}`}
            />
            <span className='whitespace-nowrap text-label-lg uppercase text-on-surface'>
              {selectedEnv}
            </span>
          </span>
          <NavArrowDown className='icon-smallest shrink-0 text-on-surface' />
        </button>
      </DropdownTrigger>
      <DropdownMenu
        aria-label='Environment selection'
        items={sortedEnvs.map(env => ({ name: env }))}
        onAction={key => changeEnvironment(String(key))}
        classNames={{
          base: '!text-label-lg text-on-surface-variant font-semibold'
        }}
      >
        {env => (
          <DropdownItem
            key={env.name}
            data-testid={`operation-header-enviroment-button-${env.name}`}
            startContent={
              <span
                className={`size-1.5 shrink-0 rounded-full ${indicatorClassName(env.name)}`}
              />
            }
            className='p-2 capitalize'
          >
            {env.name}
          </DropdownItem>
        )}
      </DropdownMenu>
    </Dropdown>
  );
};
